import { useEffect, useCallback } from 'react'
import { useAuth } from '@app/contexts/auth'
import { useGo } from '@refinedev/core'
import { useLocation } from 'react-router'
import type { TUseCanAccessProps } from '@modules/types'

export const useCanAccess = ({
  fallbackRedirect,
  allowedRoles,
  resource,
  action,
}: TUseCanAccessProps = {}) => {
  const { authLoading, authData, permissionsLoading, permissions } = useAuth()
  const go = useGo()
  const location = useLocation()

  const hasPermission = useCallback(() => {
    if (authLoading) return false

    if (authData?.error) return false

    if (!authData?.authenticated) return false

    if (allowedRoles && allowedRoles.length > 0) {
      if (!permissions || !Array.isArray(permissions)) return false
      return allowedRoles.some(role => permissions.includes(role))
    }

    if (resource && action) return true

    return true
  }, [
    authLoading,
    authData?.error,
    authData?.authenticated,
    allowedRoles,
    permissions,
    resource,
    action,
  ])

  useEffect(() => {
    if (!authLoading && !hasPermission() && fallbackRedirect) {
      if (location.pathname !== fallbackRedirect) {
        go({ to: fallbackRedirect, type: 'replace' })
      }
    }
  }, [authLoading, hasPermission, fallbackRedirect, go, location.pathname])

  const isLoading = authLoading || permissionsLoading
  const canAccess = hasPermission()

  return {
    isLoading,
    canAccess,
    hasPermission,
  }
}
